import { Link } from "react-router-dom";

import { PlayerAvatar } from "@/components/PlayerAvatar";

import type { Player } from "@/lib/api";

/**
 * Members who have written a profile.
 *
 * Built from the roster the page already loads rather than a second endpoint —
 * `has_profile` is exactly this question, and the roster is cached. Only
 * written profiles appear: a directory of blank pages would be worse than a
 * short one.
 */
export function PlayerDirectory({
  players,
}: {
  readonly players: readonly Player[];
}) {
  const written = players.filter((player) => player.has_profile);

  if (written.length === 0) {
    return (
      <p className="panel max-w-[58ch] px-5 py-4 leading-relaxed">
        Nobody has written a profile yet. Once an officer links your account to
        your roster entry, yours can be the first.
      </p>
    );
  }

  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {written.map((player) => (
        <li key={player.username}>
          <Link
            to={`/players/${encodeURIComponent(player.username)}`}
            className="slab group flex items-center gap-4 px-4 py-3"
          >
            <PlayerAvatar
              username={player.username}
              hasAvatar={player.has_avatar}
              size="h-12 w-12"
              cell={8}
              fallback={
                // aria-hidden: a visual stand-in only. Without it the row is
                // announced as "S Satilea".
                <span
                  aria-hidden="true"
                  className="display text-lg leading-none opacity-45"
                >
                  {player.display_name.charAt(0).toUpperCase()}
                </span>
              }
            />
            <span className="min-w-0">
              <span className="block truncate font-semibold group-hover:underline">
                {player.display_name}
              </span>
              {player.role ? (
                <span className="meta block text-[0.625rem] opacity-60">
                  {player.role}
                </span>
              ) : null}
            </span>
            <span
              className="meta ml-auto shrink-0 opacity-40"
              aria-hidden="true"
            >
              →
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
